import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { HideWhenSingleCompany } from "@/components/CompanyField";

const TABS = ["Procurement", "Inventory", "Billing", "Utilisation"];

export default function OperationsReports() {
  const { companies } = useCompany() || {};
  const [tab, setTab] = useState("Procurement");
  const [projects, setProjects] = useState([]);

  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);

  return (
    <div className="space-y-6" data-testid="operations-reports-page">
      <PageHeader title="Operations Reports" description="Procurement, inventory, billing and manpower/machinery utilisation." />
      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <Button key={t} size="sm" variant={tab === t ? "default" : "outline"} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}-button`}>{t}</Button>
        ))}
      </div>
      {tab === "Procurement" && <ProcurementTab companies={companies} projects={projects} />}
      {tab === "Inventory" && <InventoryTab companies={companies} />}
      {tab === "Billing" && <BillingTab projects={projects} />}
      {tab === "Utilisation" && <UtilisationTab projects={projects} />}
    </div>
  );
}

function ProcurementTab({ companies, projects }) {
  const [companyId, setCompanyId] = useState("");
  const [projectId, setProjectId] = useState("__all__");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // no Run button: the report loads when the tab opens and again whenever a filter changes
  useEffect(() => {
    let current = true;      // a slower answer for an earlier choice must not overwrite the latest one
    setLoading(true);
    api.get("/reports/procurement", { params: { company_id: companyId || undefined, project_id: projectId === "__all__" ? undefined : projectId } })
      .then(({ data }) => { if (current) setData(data); })
      .catch((err) => { if (current) toast.error(getErrorMessage(err)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [companyId, projectId]);

  return (
    <div className="space-y-4" data-testid="procurement-report-tab">
      <div className="flex items-center gap-2">
        <HideWhenSingleCompany><Select value={companyId || "__all__"} onValueChange={(v) => setCompanyId(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="procurement-report-company-select"><SelectValue placeholder="All companies" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All companies</SelectItem>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select></HideWhenSingleCompany>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-48" data-testid="procurement-report-project-select"><SelectValue placeholder="All projects" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All projects</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code}</SelectItem>)}</SelectContent>
        </Select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" data-testid="procurement-report-loading" />}
      </div>
      {!data ? (
        <EmptyState title={loading ? "Loading…" : "No data"} testId="procurement-report-empty" />
      ) : (
        <>
          <div className="rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>Vendor</TableHead><TableHead className="text-right">PO Value</TableHead><TableHead className="text-right">Received</TableHead><TableHead className="text-right">Billed</TableHead><TableHead className="text-right">Paid</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`procurement-report-row-${i}`}>
                    <TableCell className="text-foreground">{r.vendor_name}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.po_value, 2)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.received_value, 2)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.billed_value, 2)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.paid_value, 2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {data.overdue_deliveries.length > 0 && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="overdue-deliveries-panel">
              <h3 className="mb-2 text-sm font-semibold text-foreground">Overdue Deliveries</h3>
              {data.overdue_deliveries.map((d) => (
                <div key={d.po_id} className="flex justify-between py-1 text-xs text-muted-foreground">
                  <span>{d.po_number} — {d.vendor_name}</span>
                  <span className="font-mono text-foreground">₹{fmtNum(d.pending_value, 2)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InventoryTab({ companies }) {
  const [companyId, setCompanyId] = useState("");
  const [slowMovingDays, setSlowMovingDays] = useState(60);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/inventory", { params: { company_id: companyId || undefined, slow_moving_days: slowMovingDays } });
      setData(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="inventory-report-tab">
      <div className="flex items-center gap-2">
        <HideWhenSingleCompany><Select value={companyId || "__all__"} onValueChange={(v) => setCompanyId(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="inventory-report-company-select"><SelectValue placeholder="All companies" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All companies</SelectItem>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select></HideWhenSingleCompany>
        <Input type="number" value={slowMovingDays} onChange={(e) => setSlowMovingDays(e.target.value)} className="w-32" data-testid="slow-moving-days-input" />
        <Button onClick={load} disabled={loading} data-testid="inventory-report-run-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}</Button>
      </div>
      {!data ? (
        <EmptyState title="Run the report" testId="inventory-report-empty" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-2 flex justify-between"><h3 className="text-sm font-semibold text-foreground">Stock Valuation</h3><span className="font-mono text-xs text-muted-foreground">₹{fmtNum(data.total_valuation, 2)}</span></div>
            <Table>
              <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Value</TableHead></TableRow></TableHeader>
              <TableBody>{data.valuation.map((r, i) => <TableRow key={i}><TableCell className="text-foreground">{r.item_name}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.qty, 2)}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.value, 2)}</TableCell></TableRow>)}</TableBody>
            </Table>
          </div>
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-2"><h3 className="text-sm font-semibold text-foreground">Slow Moving ({slowMovingDays}+ days idle)</h3></div>
            {data.slow_moving.length === 0 ? <EmptyState title="Nothing slow moving" testId="slow-moving-empty" /> : (
              <Table><TableBody>{data.slow_moving.map((r, i) => <TableRow key={i} data-testid={`slow-moving-row-${i}`}><TableCell className="text-foreground">{r.item_name}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.value, 2)}</TableCell></TableRow>)}</TableBody></Table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BillingTab({ projects }) {
  const [projectId, setProjectId] = useState("__all__");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // no Run button: the report loads when the tab opens and again whenever the project changes
  useEffect(() => {
    let current = true;
    setLoading(true);
    api.get("/reports/billing", { params: { project_id: projectId === "__all__" ? undefined : projectId } })
      .then(({ data }) => { if (current) setData(data); })
      .catch((err) => { if (current) toast.error(getErrorMessage(err)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [projectId]);

  return (
    <div className="space-y-4" data-testid="billing-report-tab">
      <div className="flex items-center gap-2">
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-56" data-testid="billing-report-project-select"><SelectValue placeholder="All projects" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All projects</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
        </Select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" data-testid="billing-report-loading" />}
      </div>
      {!data ? <EmptyState title={loading ? "Loading…" : "No data"} testId="billing-report-empty" /> : (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow><TableHead>Project</TableHead><TableHead className="text-right">Contract</TableHead><TableHead className="text-right">Certified</TableHead><TableHead className="text-right">Billed</TableHead><TableHead className="text-right">Received</TableHead><TableHead className="text-right">Retention</TableHead></TableRow></TableHeader>
            <TableBody>{data.rows.map((r) => <TableRow key={r.project_id} data-testid={`billing-report-row-${r.project_code}`}><TableCell className="text-foreground">{r.project_code} — {r.project_name}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.contract_value, 2)}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.certified, 2)}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.billed, 2)}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.received, 2)}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.retention_held, 2)}</TableCell></TableRow>)}</TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function UtilisationTab({ projects }) {
  const [projectId, setProjectId] = useState("");
  const [dateFrom, setDateFrom] = useState(todayISO().slice(0, 8) + "01");
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/reports/utilisation", { params: { project_id: projectId, date_from: dateFrom, date_to: dateTo } });
      setData(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="utilisation-report-tab">
      <div className="flex items-center gap-2">
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-56" data-testid="utilisation-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
          <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" data-testid="utilisation-date-from-input" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" data-testid="utilisation-date-to-input" />
        <Button onClick={load} disabled={loading} data-testid="utilisation-run-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}</Button>
      </div>
      {!data ? <EmptyState title="Select a project and run" testId="utilisation-empty" /> : (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-2"><h3 className="text-sm font-semibold text-foreground">Manpower & Machinery</h3></div>
            {data.manpower_machinery.length === 0 ? <EmptyState title="No resource actuals in this range" testId="utilisation-manpower-empty" /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Trade</TableHead><TableHead className="text-right">Planned</TableHead><TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Utilisation</TableHead></TableRow></TableHeader>
                <TableBody>{data.manpower_machinery.map((r, i) => (
                  <TableRow key={i} data-testid={`utilisation-row-${i}`}>
                    <TableCell className="text-foreground">{r.trade} {r.is_machinery && <Badge variant="secondary" className="ml-1 text-[10px]">Machinery</Badge>}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.planned_total, 1)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.actual_total, 1)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.utilisation_pct, 1)}%</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
          </div>
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-2"><h3 className="text-sm font-semibold text-foreground">Equipment Logbook Summary</h3></div>
            {data.equipment_logbook_summary.length === 0 ? <EmptyState title="No logbook entries in this range" testId="utilisation-logbook-empty" /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Equipment</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">KM</TableHead><TableHead className="text-right">Fuel</TableHead></TableRow></TableHeader>
                <TableBody>{data.equipment_logbook_summary.map((r, i) => <TableRow key={i}><TableCell className="text-foreground">{r.equipment_code} — {r.equipment_name}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.total_hours, 1)}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.total_km, 1)}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.total_fuel_litres, 1)}</TableCell></TableRow>)}</TableBody>
              </Table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
